/**
 * VerificationQuestions.js
 * 
 * Database câu hỏi verification cho từng Syndrome Pattern
 * Mỗi pattern có 3-5 câu hỏi quan trọng nhất để verify
 * 
 * Dùng sau khi AI phân tích hình ảnh và đưa ra suspected patterns
 */

// Cấu trúc câu hỏi
// - id: unique identifier
// - text: Câu hỏi tiếng Việt
// - weight: Trọng số (1-3, 3 = quan trọng nhất)
// - type: 'boolean' (Có/Không) hoặc 'frequency' (Không bao giờ → Luôn luôn)

export const VERIFICATION_QUESTIONS = {
    // Khí Hư (Qi Deficiency)
    qi_deficiency: [
        {
            id: 'v_qd1',
            text: 'Bạn có thường xuyên cảm thấy mệt mỏi, thiếu năng lượng không?',
            weight: 3,
            type: 'frequency'
        },
        {
            id: 'v_qd2',
            text: 'Bạn có hay đổ mồ hôi tự nhiên (không do vận động) không?',
            weight: 2,
            type: 'frequency'
        },
        {
            id: 'v_qd3',
            text: 'Giọng nói của bạn có yếu, nhỏ không?',
            weight: 2,
            type: 'boolean'
        },
        {
            id: 'v_qd4',
            text: 'Bạn có dễ bị cảm lạnh không?',
            weight: 2,
            type: 'frequency'
        }
    ],

    // Dương Hư (Yang Deficiency)
    yang_deficiency: [
        {
            id: 'v_yd1',
            text: 'Bạn có sợ lạnh hơn người khác không?',
            weight: 3,
            type: 'boolean'
        },
        {
            id: 'v_yd2',
            text: 'Tay chân bạn có thường xuyên lạnh không?',
            weight: 3,
            type: 'frequency'
        },
        {
            id: 'v_yd3',
            text: 'Bạn có hay đau bụng hoặc tiêu chảy khi ăn đồ lạnh không?',
            weight: 2,
            type: 'frequency'
        },
        {
            id: 'v_yd4',
            text: 'Bạn có thích uống nước ấm/nóng không?',
            weight: 2,
            type: 'boolean'
        }
    ],

    // Âm Hư (Yin Deficiency)
    yin_deficiency: [
        {
            id: 'v_yind1',
            text: 'Lòng bàn tay, bàn chân bạn có hay nóng không?',
            weight: 3,
            type: 'frequency'
        },
        {
            id: 'v_yind2',
            text: 'Bạn có hay bị khô miệng, khô họng không?',
            weight: 3,
            type: 'frequency'
        },
        {
            id: 'v_yind3',
            text: 'Bạn có hay bị ra mồ hôi trộm (đổ mồ hôi khi ngủ) không?',
            weight: 2,
            type: 'frequency'
        },
        {
            id: 'v_yind4',
            text: 'Bạn có hay cảm thấy nóng trong người về chiều/tối?',
            weight: 2,
            type: 'frequency'
        }
    ],

    // Huyết Hư (Blood Deficiency)
    blood_deficiency: [
        {
            id: 'v_bd1',
            text: 'Bạn có hay bị chóng mặt, hoa mắt không?',
            weight: 3,
            type: 'frequency'
        },
        {
            id: 'v_bd2',
            text: 'Da và móng tay bạn có nhợt nhạt không?',
            weight: 2,
            type: 'boolean'
        },
        {
            id: 'v_bd3',
            text: 'Bạn có hay bị mất ngủ, khó ngủ không?',
            weight: 2,
            type: 'frequency'
        },
        {
            id: 'v_bd4',
            text: '(Nữ) Kinh nguyệt có ít và nhạt màu không?',
            weight: 2,
            type: 'boolean',
            conditional: 'female'
        }
    ],

    // Huyết Ứ (Blood Stasis)
    blood_stasis: [
        {
            id: 'v_bs1',
            text: 'Bạn có hay bị đau nhức cố định một chỗ không?',
            weight: 3,
            type: 'frequency'
        },
        {
            id: 'v_bs2',
            text: 'Cơn đau có như kim châm hoặc dao cắt không?',
            weight: 3,
            type: 'boolean'
        },
        {
            id: 'v_bs3',
            text: 'Môi bạn có màu thâm tím không?',
            weight: 2,
            type: 'boolean'
        },
        {
            id: 'v_bs4',
            text: 'Da bạn có sạm màu hoặc có đốm nâu không?',
            weight: 2,
            type: 'boolean'
        }
    ],

    // Khí Uất (Qi Stagnation)
    qi_stagnation: [
        {
            id: 'v_qs1',
            text: 'Bạn có hay cảm thấy lo âu, buồn phiền không?',
            weight: 3,
            type: 'frequency'
        },
        {
            id: 'v_qs2',
            text: 'Ngực và sườn có hay cảm thấy tức không?',
            weight: 3,
            type: 'frequency'
        },
        {
            id: 'v_qs3',
            text: 'Bạn có hay thở dài không?',
            weight: 2,
            type: 'frequency'
        },
        {
            id: 'v_qs4',
            text: 'Bạn có dễ cáu gắt, nổi giận không?',
            weight: 2,
            type: 'frequency'
        }
    ],

    // Thấp Nhiệt (Damp-Heat)
    damp_heat: [
        {
            id: 'v_dh1',
            text: 'Da bạn có hay nổi mụn nhọt không?',
            weight: 3,
            type: 'frequency'
        },
        {
            id: 'v_dh2',
            text: 'Bạn có hay cảm thấy miệng đắng, miệng hôi không?',
            weight: 2,
            type: 'frequency'
        },
        {
            id: 'v_dh3',
            text: 'Phân của bạn có hay dính và hôi không?',
            weight: 2,
            type: 'boolean'
        },
        {
            id: 'v_dh4',
            text: 'Nước tiểu có vàng đậm và ít không?',
            weight: 2,
            type: 'boolean'
        }
    ],

    // Đàm Thấp (Phlegm-Dampness)
    phlegm_dampness: [
        {
            id: 'v_pd1',
            text: 'Bạn có cảm thấy người nặng nề, uể oải không?',
            weight: 3,
            type: 'frequency'
        },
        {
            id: 'v_pd2',
            text: 'Bạn có hay bị đờm nhiều không?',
            weight: 3,
            type: 'frequency'
        },
        {
            id: 'v_pd3',
            text: 'Da mặt bạn có hay tiết dầu nhiều không?',
            weight: 2,
            type: 'boolean'
        },
        {
            id: 'v_pd4',
            text: 'Bạn có cảm thấy miệng nhớt không?',
            weight: 2,
            type: 'boolean'
        }
    ],

    // Tỳ Hư (Spleen Deficiency)
    spleen_deficiency: [
        {
            id: 'v_sd1',
            text: 'Bạn có hay bị đầy bụng, ợ hơi sau ăn không?',
            weight: 3,
            type: 'frequency'
        },
        {
            id: 'v_sd2',
            text: 'Phân của bạn có hay lỏng hoặc không đều không?',
            weight: 3,
            type: 'frequency'
        },
        {
            id: 'v_sd3',
            text: 'Bạn có hay chán ăn, không thèm ăn không?',
            weight: 2,
            type: 'frequency'
        },
        {
            id: 'v_sd4',
            text: 'Chân tay bạn có hay phù nhẹ không?',
            weight: 2,
            type: 'boolean'
        }
    ],

    // Can Hỏa (Liver Fire)
    liver_fire: [
        {
            id: 'v_lf1',
            text: 'Bạn có hay bị đau đầu, nhất là hai bên thái dương không?',
            weight: 3,
            type: 'frequency'
        },
        {
            id: 'v_lf2',
            text: 'Mắt bạn có hay đỏ hoặc khô không?',
            weight: 2,
            type: 'boolean'
        },
        {
            id: 'v_lf3',
            text: 'Bạn có dễ cáu giận, nổi nóng không?',
            weight: 3,
            type: 'frequency'
        },
        {
            id: 'v_lf4',
            text: 'Bạn có hay bị miệng đắng buổi sáng không?',
            weight: 2,
            type: 'boolean'
        }
    ],

    // Thận Hư (Kidney Deficiency)
    kidney_deficiency: [
        {
            id: 'v_kd1',
            text: 'Bạn có hay bị đau lưng, mỏi gối không?',
            weight: 3,
            type: 'frequency'
        },
        {
            id: 'v_kd2',
            text: 'Bạn có hay bị ù tai hoặc nghe kém không?',
            weight: 2,
            type: 'frequency'
        },
        {
            id: 'v_kd3',
            text: 'Bạn có hay tiểu đêm nhiều lần không?',
            weight: 3,
            type: 'frequency'
        },
        {
            id: 'v_kd4',
            text: 'Tóc bạn có bạc sớm hoặc rụng nhiều không?',
            weight: 2,
            type: 'boolean'
        }
    ],

    // Tâm Hỏa (Heart Fire)
    heart_fire: [
        {
            id: 'v_hf1',
            text: 'Bạn có hay bị mất ngủ, khó ngủ không?',
            weight: 3,
            type: 'frequency'
        },
        {
            id: 'v_hf2',
            text: 'Bạn có hay cảm thấy bồn chồn, lo lắng không?',
            weight: 3,
            type: 'frequency'
        },
        {
            id: 'v_hf3',
            text: 'Lưỡi bạn có hay bị lở loét không?',
            weight: 2,
            type: 'boolean'
        },
        {
            id: 'v_hf4',
            text: 'Nước tiểu có vàng đậm và nóng rát không?',
            weight: 2,
            type: 'boolean'
        }
    ]
};

// Answer scoring
export const ANSWER_SCORES = {
    frequency: {
        never: 0,
        rarely: 1,
        sometimes: 2,
        often: 3,
        always: 4
    },
    boolean: {
        no: 0,
        yes: 3
    }
};

// Helper functions
export const VerificationHelper = {
    /**
     * Get all question IDs for a pattern
     */
    getQuestionIds(patternId) {
        const questions = VERIFICATION_QUESTIONS[patternId];
        return questions ? questions.map(q => q.id) : [];
    },

    /**
     * Calculate verification score for a pattern
     */
    calculatePatternScore(patternId, answers) {
        const questions = VERIFICATION_QUESTIONS[patternId];
        if (!questions) return 0;

        let totalScore = 0;
        let maxScore = 0;

        questions.forEach(q => {
            const answer = answers[q.id];
            if (answer !== undefined) {
                const scoreTable = ANSWER_SCORES[q.type];
                const answerScore = scoreTable[answer] || 0;
                totalScore += answerScore * q.weight;
            }
            // Max possible score
            const maxAnswerScore = q.type === 'frequency' ? 4 : 3;
            maxScore += maxAnswerScore * q.weight;
        });

        return maxScore > 0 ? (totalScore / maxScore) * 100 : 0;
    },

    /**
     * Adjust AI confidence based on verification answers
     */
    adjustConfidence(originalConfidence, verificationScore) {
        // If verification score is high, increase confidence
        // If low, decrease confidence
        const adjustment = (verificationScore - 50) / 100;
        const newConfidence = originalConfidence + (adjustment * 0.3);

        // Clamp between 0 and 1
        return Math.max(0, Math.min(1, newConfidence));
    },

    /**
     * Get patterns that need more questions
     */
    getPatternsNeedingVerification(matchedPatterns) {
        // Only verify patterns with confidence between 40-80%
        // Very high (>80%) or very low (<40%) don't need verification
        return matchedPatterns.filter(p =>
            p.confidence >= 0.4 && p.confidence <= 0.8
        );
    }
};

export default {
    VERIFICATION_QUESTIONS,
    ANSWER_SCORES,
    VerificationHelper
};
